/* eslint-disable no-plusplus */
import { useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import { ApolloError, useQuery } from '@apollo/client';

import {
  GetSponsoredProductsDocument,
  GetSponsoredProductsPlacementsDocument,
  SponsoredProductsPlacementsPage,
} from 'src/gql/graphql';
import type { GetSponsoredProductsInput } from 'src/gql/graphql';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import { parseProduct, Product } from 'src/utils/helpers/product';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { ADS_FETCH_POLICY } from 'src/utils/ads/helpers';
import { useFiltersForSponsoredResources } from 'src/dispensary/hooks/use-filters-for-sponsored-resources';
import { MIN_SEARCH_LENGTH } from 'src/constants/common';
import { useSponsoredProductsFilter } from './use-decorate-with-sponsored-products';

type DecorateWithSponsoredProductsArgs = Omit<GetSponsoredProductsInput, 'dispensaryId'> & {
  error: ApolloError | undefined;
  loading: boolean;
  products: Product[];
  skip?: boolean;
};

type DecorateWithSponsoredProducts = {
  error: ApolloError | undefined;
  loading: boolean;
  products: Product[];
};

export function useDecorateCategoryPageWithSponsoredProducts({
  category,
  error: productsError,
  inventoryId,
  limit,
  loading: productsLoading,
  productId,
  products,
  searchQuery,
  skip = true,
}: DecorateWithSponsoredProductsArgs): DecorateWithSponsoredProducts {
  const UI = useUI();
  const { queryParams: qp, defaults } = useProductQueryParams();
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id;
  const safeCategory = category ?? undefined;

  const isCategoryPage = !qp.search;

  const { data: placementsData, loading: placementsLoading } = useQuery(GetSponsoredProductsPlacementsDocument, {
    fetchPolicy: 'cache-first',
    skip: !dispensaryId,
    variables: {
      input: {
        page: isCategoryPage ? SponsoredProductsPlacementsPage.category : SponsoredProductsPlacementsPage.search,
        uiVariant: UI.variant,
        dispensaryId: dispensaryId as string,
      },
    },
  });

  const adPlacements = useMemo(() => placementsData?.getSponsoredProductsPlacements.placements ?? [], [placementsData]);

  const hasAdPlacements = adPlacements.length > 0;

  const { filters } = useFiltersForSponsoredResources();

  const invalidSearchQuery = Boolean(searchQuery && searchQuery.length < MIN_SEARCH_LENGTH);

  const skipQuery =
    skip ||
    limit < 1 ||
    !dispensaryId ||
    Boolean(productsError) ||
    !hasAdPlacements ||
    (!isCategoryPage && invalidSearchQuery);

  const { data, loading: sponsoredProductsLoading, error: sponsoredProductsError } = useQuery(
    GetSponsoredProductsDocument,
    {
      fetchPolicy: ADS_FETCH_POLICY,
      variables: {
        input: {
          limit,
          dispensaryId,
          inventoryId,
          category: safeCategory,
          searchQuery,
          productId,
          filters,
        },
      },
      skip: skipQuery,
    }
  );

  const unfilteredSponsoredProductsData: Product[] = data?.getSponsoredProducts as Product[];
  const { query } = useRouter() ?? {};

  const sponsoredProductsData = useSponsoredProductsFilter(
    unfilteredSponsoredProductsData,
    query,
    qp,
    defaults,
    category ?? '',
    dispensaryId
  );

  const serializedProducts = useMemo(() => {
    if (productsLoading || placementsLoading || sponsoredProductsLoading || productsError) {
      return [];
    }

    if (skip || !sponsoredProductsData.length || sponsoredProductsError) {
      return products;
    }

    const parsedSponsoredProducts = sponsoredProductsData.map(parseProduct);

    return buildDecoratedProductList(products, parsedSponsoredProducts, adPlacements);
  }, [
    adPlacements,
    placementsLoading,
    products,
    productsError,
    productsLoading,
    skip,
    sponsoredProductsData,
    sponsoredProductsError,
    sponsoredProductsLoading,
  ]);

  return {
    products: serializedProducts,
    loading: productsLoading || placementsLoading || sponsoredProductsLoading,
    error: productsError,
  };
}

export function buildDecoratedProductList(
  organicProducts: Product[],
  sponsoredProducts: Product[],
  placementIndices: number[]
): Product[] {
  if (placementIndices.length === 0 || sponsoredProducts.length === 0) {
    return organicProducts;
  }

  // First, identify which sponsored products need to be removed from organic list
  const sponsoredIds = new Set(sponsoredProducts.map((p) => p.id));
  const filteredOrganic = organicProducts.filter((p) => !sponsoredIds.has(p.id));

  // Now build the final list with sponsored products at exact indices
  const result: Product[] = [];
  let organicIndex = 0;
  let sponsoredIndex = 0;

  for (let i = 0; i < filteredOrganic.length + sponsoredProducts.length; i++) {
    if (placementIndices.includes(i) && sponsoredIndex < sponsoredProducts.length) {
      result.push(sponsoredProducts[sponsoredIndex++]);
    } else if (organicIndex < filteredOrganic.length) {
      result.push(filteredOrganic[organicIndex++]);
    }
  }

  // Add any remaining organic products that didn't fit in the loop
  while (organicIndex < filteredOrganic.length) {
    result.push(filteredOrganic[organicIndex++]);
  }

  return result;
}
